// ============================================================================
//  CONFIG DAS IMAGENS OG (open graph) POR LANDING × IDIOMA
//
//  Fonte unica da verdade para:
//  - O gerador de PNGs (scripts/generate-og-images.ts) — renderiza um card de
//    marca 1200×630 por (landing × idioma) em public/og/<routeId>-<locale>.png
//  - O componente SEO das landings (ogImageUrl) — aponta og:image/twitter:image
//    pra imagem certa do idioma ativo.
//
//  Por que existe (em vez de uma og:image generica): preview de link por
//  competicao/idioma melhora o CTR de compartilhamento (WhatsApp/Facebook/X) e
//  fica consistente com os cards dos posts do blog. PNG (nao SVG) porque redes
//  sociais nao renderizam SVG como og:image.
//
//  Ao adicionar uma landing aqui: rode `npm run gen:og` e commite os PNGs.
// ============================================================================

import type { AppLocale } from '../i18n';

export const SITE_URL = 'https://bolaoelite.com.br';

/** Tipo de card — define kicker e tagline (derivados, nao repetidos por landing). */
type OgCardType = 'competition' | 'simulator';

export interface OgCard {
  type: OgCardType;
  /** Titulo curto e curado por idioma (vai grande no card). */
  title: Record<AppLocale, string>;
}

/**
 * Cards por `routeId` (chave de ROUTE_SLUGS / landing.routeId). Só landings
 * listadas aqui ganham og:image dedicada; as demais caem na og-image.png
 * generica (ogImageUrl retorna undefined → default do componente SEO).
 */
export const OG_CARDS: Record<string, OgCard> = {
  // Competicoes
  'pool-copa-do-mundo': {
    type: 'competition',
    title: { 'pt-BR': 'Copa do Mundo 2026', en: 'World Cup 2026', es: 'Mundial 2026' },
  },
  'pool-brasileirao': {
    type: 'competition',
    title: { 'pt-BR': 'Brasileirão 2026', en: 'Brazilian League 2026', es: 'Brasileirão 2026' },
  },
  'pool-copa-do-brasil': {
    type: 'competition',
    title: { 'pt-BR': 'Copa do Brasil 2026', en: 'Copa do Brasil 2026', es: 'Copa de Brasil 2026' },
  },
  'pool-libertadores': {
    type: 'competition',
    title: { 'pt-BR': 'Libertadores 2026', en: 'Libertadores 2026', es: 'Libertadores 2026' },
  },
  'pool-champions-league': {
    type: 'competition',
    title: { 'pt-BR': 'Champions League 2026', en: 'Champions League 2026', es: 'Champions League 2026' },
  },
  'pool-carioca': {
    type: 'competition',
    title: { 'pt-BR': 'Campeonato Carioca 2026', en: 'Rio State League 2026', es: 'Campeonato Carioca 2026' },
  },
  'pool-paulista': {
    type: 'competition',
    title: { 'pt-BR': 'Campeonato Paulista 2026', en: 'São Paulo State League 2026', es: 'Campeonato Paulista 2026' },
  },
  'pool-estaduais': {
    type: 'competition',
    title: { 'pt-BR': 'Estaduais 2026', en: 'State Leagues 2026', es: 'Estatales 2026' },
  },

  // Simuladores
  'sim-copa-do-mundo': {
    type: 'simulator',
    title: { 'pt-BR': 'Simulador da Copa 2026', en: 'World Cup Simulator 2026', es: 'Simulador del Mundial 2026' },
  },
  'sim-libertadores': {
    type: 'simulator',
    title: { 'pt-BR': 'Simulador da Libertadores', en: 'Libertadores Simulator', es: 'Simulador de la Libertadores' },
  },
  'sim-sudamericana': {
    type: 'simulator',
    title: { 'pt-BR': 'Simulador da Sudamericana', en: 'Sudamericana Simulator', es: 'Simulador de la Sudamericana' },
  },
};

/** Kicker (sobretítulo) por tipo × idioma. */
export const OG_KICKER: Record<OgCardType, Record<AppLocale, string>> = {
  competition: { 'pt-BR': 'BOLÃO', en: 'POOL', es: 'QUINIELA' },
  simulator: { 'pt-BR': 'SIMULADOR', en: 'SIMULATOR', es: 'SIMULADOR' },
};

/** Tagline (subtítulo) por tipo × idioma. */
export const OG_TAGLINE: Record<OgCardType, Record<AppLocale, string>> = {
  competition: {
    'pt-BR': 'Crie seu bolão grátis',
    en: 'Create your free pool',
    es: 'Crea tu quiniela gratis',
  },
  simulator: {
    'pt-BR': 'Monte seu bracket grátis',
    en: 'Build your bracket free',
    es: 'Arma tu cuadro gratis',
  },
};

/** Nome do arquivo PNG de uma landing/idioma (sem path). */
export function ogImageFile(routeId: string, locale: AppLocale): string {
  return `${routeId}-${locale}.png`;
}

/**
 * URL absoluta da og:image dedicada da landing no idioma — ou undefined se a
 * landing não tem card (cai na og-image.png genérica via default do SEO).
 */
export function ogImageUrl(routeId: string | undefined, locale: AppLocale): string | undefined {
  if (!routeId || !OG_CARDS[routeId]) return undefined;
  return `${SITE_URL}/og/${ogImageFile(routeId, locale)}`;
}
